import React from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import { ALL_USERS } from "AppConstants";

/**
 * Controlled, presentational user-filter dropdown shared by every screen that scopes a
 * report to one person. Renders "All" → optional manager "(Me)" → grouped
 * "Members"/"Managers" optgroups when `grouped`; otherwise a flat member list. Each
 * optgroup only appears when it has entries, so a manager with no L1s still shows a
 * "Members" heading. Holds no state.
 * @since NEXT_VERSION
 */
export const UserFilterSelect = ({
  value,
  onChange,
  memberOptions = [],
  managerOptions = [],
  selfOption = null,
  includeAll = true,
  grouped = false,
  label = "Member / Manager",
  id = "user-filter",
  // A screen whose report covers a whole scope rather than one person disables this and says so:
  // the Consolidated export is scope-driven, so a single selection has no meaning for it.
  // @since NEXT_VERSION
  disabled = false,
  helperText,
  sx,
}) => (
  <TextField
    select
    id={id}
    label={label}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    helperText={helperText}
    InputLabelProps={{ shrink: true }}
    SelectProps={{ native: true }}
    sx={sx ?? { flex: { xs: "1 1 140px", md: "0 0 170px" }, minWidth: 0 }}
  >
    {includeAll && <option value={ALL_USERS}>All</option>}
    {selfOption && <option value={selfOption.value}>{selfOption.label}</option>}
    {grouped ? (
      <>
        {memberOptions.length > 0 && (
          <optgroup label="Members">
            {memberOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        )}
        {managerOptions.length > 0 && (
          <optgroup label="Managers">
            {managerOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        )}
      </>
    ) : (
      memberOptions.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))
    )}
  </TextField>
);

UserFilterSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  memberOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  managerOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  selfOption: PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  includeAll: PropTypes.bool,
  grouped: PropTypes.bool,
  label: PropTypes.string,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  sx: PropTypes.object,
};

export default UserFilterSelect;
